import { useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { BarChart3, Eye, EyeOff, GitBranch, Kanban, Plus, Search, Table2 } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermissions';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { JobsBoardView as JobsBoardLens } from '@/components/organisms/jobs/JobsBoardView';
import { EntityIndexLayout, JobsWorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { EntityViewToggle } from '@/components/molecules';
import { useJobPipelines } from '@/hooks/pipelines/useJobPipelines';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import {
  DEFAULT_JOB_LIST_FILTERS,
  JOB_SORT_OPTIONS,
  JOB_STATUS_FILTERS,
  JobListPanel,
  resolveJobListFilters,
  type JobListFilters,
} from '@/components/organisms/jobs/JobListPanel';
import { JobTableView } from '@/components/organisms/jobs/JobTableView';
import { Fullbleed } from '@/contexts/fullbleed';
import { preserveServiceCallWorkspace } from '@/lib/jobs/serviceCallWorkspace';

type ViewMode = 'split' | 'table' | 'board';
type JobRouteSearch = Partial<JobListFilters> & { serviceCallWorkspace?: boolean };
const JOB_PRIORITY_VALUES = ['all', 'LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
const JOB_TOTAL_OP_VALUES = ['eq', 'gt', 'lt', 'gte', 'lte'] as const;

export const Route = createFileRoute('/_dashboard/dashboard/jobs')({
  validateSearch: (search: Record<string, unknown>): JobRouteSearch => ({
    q: typeof search.q === 'string' ? search.q : DEFAULT_JOB_LIST_FILTERS.q,
    status: JOB_STATUS_FILTERS.some((filter) => filter.value === search.status)
      ? (search.status as JobListFilters['status'])
      : DEFAULT_JOB_LIST_FILTERS.status,
    jobTypeId:
      typeof search.jobTypeId === 'string' && search.jobTypeId
        ? search.jobTypeId
        : DEFAULT_JOB_LIST_FILTERS.jobTypeId,
    businessUnitId:
      typeof search.businessUnitId === 'string' && search.businessUnitId
        ? search.businessUnitId
        : DEFAULT_JOB_LIST_FILTERS.businessUnitId,
    priority: JOB_PRIORITY_VALUES.includes(search.priority as (typeof JOB_PRIORITY_VALUES)[number])
      ? (search.priority as JobListFilters['priority'])
      : DEFAULT_JOB_LIST_FILTERS.priority,
    assignedTo:
      typeof search.assignedTo === 'string'
        ? search.assignedTo
        : DEFAULT_JOB_LIST_FILTERS.assignedTo,
    grandTotalOp: JOB_TOTAL_OP_VALUES.includes(
      search.grandTotalOp as (typeof JOB_TOTAL_OP_VALUES)[number],
    )
      ? (search.grandTotalOp as JobListFilters['grandTotalOp'])
      : DEFAULT_JOB_LIST_FILTERS.grandTotalOp,
    grandTotalValue:
      typeof search.grandTotalValue === 'string'
        ? search.grandTotalValue
        : DEFAULT_JOB_LIST_FILTERS.grandTotalValue,
    page: typeof search.page === 'number' && search.page > 0 ? Math.floor(search.page) : 1,
    sort:
      typeof search.sort === 'number' && search.sort >= 0
        ? Math.min(Math.floor(search.sort), JOB_SORT_OPTIONS.length - 1)
        : DEFAULT_JOB_LIST_FILTERS.sort,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    renewalDue: search.renewalDue === true || search.renewalDue === 'true',
    serviceCall: search.serviceCall === true || search.serviceCall === 'true',
    serviceCallWorkspace:
      search.serviceCallWorkspace === true || search.serviceCallWorkspace === 'true',
    hotspotPostalArea:
      typeof search.hotspotPostalArea === 'string' ? search.hotspotPostalArea : undefined,
    hotspotJobTypeId:
      typeof search.hotspotJobTypeId === 'string' ? search.hotspotJobTypeId : undefined,
    hotspotTechnicianId:
      typeof search.hotspotTechnicianId === 'string' ? search.hotspotTechnicianId : undefined,
    hotspotCountry:
      search.hotspotCountry === 'US' || search.hotspotCountry === 'CA'
        ? search.hotspotCountry
        : undefined,
    hotspotRegionCode:
      typeof search.hotspotRegionCode === 'string' ? search.hotspotRegionCode : undefined,
  }),
  component: JobsLayout,
});

function getStoredView(): ViewMode {
  try {
    const value = localStorage.getItem('jobs-view');
    if (value === 'table' || value === 'board') return value;
  } catch {
    /* no-op */
  }
  // NOT-297: the List (split) view was removed; Table is the default and any
  // legacy 'list'/'split' preference falls back to it.
  return 'table';
}

function cleanSearch(next: JobRouteSearch) {
  const clean: JobRouteSearch = { ...next };
  const defaults: Partial<Record<keyof JobRouteSearch, unknown>> = {
    q: '',
    status: 'all',
    jobTypeId: 'all',
    priority: 'all',
    assignedTo: '',
    grandTotalOp: 'eq',
    grandTotalValue: '',
    page: 1,
    sort: 0,
    renewalDue: false,
    serviceCall: false,
    serviceCallWorkspace: false,
  };

  (Object.keys(clean) as Array<keyof JobRouteSearch>).forEach((key) => {
    if (clean[key] == null || clean[key] === defaults[key]) delete clean[key];
  });

  return clean as JobRouteSearch;
}

function JobsLayout() {
  const { location } = useRouterState();
  const router = useRouter();
  const routeSearch = Route.useSearch();
  const isServiceCallsRoute = location.pathname === '/dashboard/jobs/service-calls';
  // `serviceCall` is also a removable filter in All Jobs, so it cannot identify
  // the dedicated workspace. Carry a separate origin marker only while a user
  // drills into a Job from the stable Service Calls route.
  const isServiceCallsWorkspace = isServiceCallsRoute || routeSearch.serviceCallWorkspace === true;
  const filters = useMemo(
    () => ({
      ...resolveJobListFilters(routeSearch),
      ...(isServiceCallsRoute ? { serviceCall: true } : {}),
    }),
    [isServiceCallsRoute, routeSearch],
  );
  const {
    hotspotPostalArea,
    hotspotJobTypeId,
    hotspotTechnicianId,
    hotspotCountry,
    hotspotRegionCode,
  } = routeSearch;
  const [boardMode, setBoardMode] = useState(() => getStoredView() === 'board');

  const idMatch = location.pathname.match(/\/dashboard\/jobs\/([^/]+)/);
  const selectedId =
    idMatch && idMatch[1] !== 'new' && idMatch[1] !== 'reports' && idMatch[1] !== 'service-calls'
      ? idMatch[1]
      : undefined;
  const isReportsRoute = location.pathname.includes('/dashboard/jobs/reports');
  const isNewRoute = location.pathname.includes('/dashboard/jobs/new');
  const isJobEditRoute = /\/dashboard\/jobs\/[^/]+\/edit\/?$/.test(location.pathname);
  const isAppointmentEditRoute = /\/dashboard\/jobs\/[^/]+\/appointments\/[^/]+\/edit\/?$/.test(
    location.pathname,
  );
  const isAppointmentNewRoute = /\/dashboard\/jobs\/[^/]+\/appointments\/new\/?$/.test(
    location.pathname,
  );
  const isDedicatedTaskRoute =
    /\/dashboard\/jobs\/[^/]+\/(change-orders|expenses|payment-schedule|invoices|appointments)\/[^/]+(\/edit)?\/?$/.test(
      location.pathname,
    );
  const isDedicatedJobFormRoute = isNewRoute || isJobEditRoute;
  const isFormMode = isDedicatedJobFormRoute || isAppointmentEditRoute || isAppointmentNewRoute;
  const hasChildRoute = Boolean(selectedId) || isNewRoute;

  function withWorkspaceContext(next: JobRouteSearch) {
    return cleanSearch(preserveServiceCallWorkspace(next, isServiceCallsWorkspace));
  }

  const listSearch = withWorkspaceContext(filters);

  function handleSelect(id: string) {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: listSearch,
    });
  }

  function handleFiltersChange(patch: Partial<JobListFilters>) {
    // Stay on the currently-open route (e.g. /dashboard/jobs/$id) and update
    // search in place. Setting `to: '/dashboard/jobs'` routed every page/filter
    // change through the index's beforeLoad redirect, which drops `search` and
    // resets the pager to page 1 — matching the working estimates sibling.
    void router.navigate({
      // @ts-expect-error — search required by TanStack Router v1.168
      search: () => {
        // The current pathname may be a leaf Job/Visit route whose validator
        // only owns detail keys. Read the parent route's search instead of the
        // leaf updater value so list filters and the Service Calls origin do
        // not disappear after an in-detail filter edit.
        const current = resolveJobListFilters(routeSearch);
        if (
          hotspotPostalArea &&
          (Object.hasOwn(patch, 'dateFrom') || Object.hasOwn(patch, 'dateTo'))
        ) {
          const nextDateFrom = Object.hasOwn(patch, 'dateFrom') ? patch.dateFrom : current.dateFrom;
          const nextDateTo = Object.hasOwn(patch, 'dateTo') ? patch.dateTo : current.dateTo;

          if (!nextDateFrom && !nextDateTo) {
            return withWorkspaceContext({
              ...current,
              ...patch,
              hotspotPostalArea: undefined,
              hotspotJobTypeId: undefined,
              hotspotTechnicianId: undefined,
              hotspotCountry: undefined,
              hotspotRegionCode: undefined,
            });
          }

          // Date pickers emit an incomplete range while the second date is
          // being chosen. Keep the current report period until both bounds
          // exist so the hotspot query can never become unbounded.
          if (!nextDateFrom || !nextDateTo) return withWorkspaceContext(current);
        }

        return withWorkspaceContext({
          ...current,
          ...patch,
        });
      },
      replace: true,
    });
  }

  function showBoard() {
    try {
      localStorage.setItem('jobs-view', 'board');
    } catch {
      /* no-op */
    }
    setBoardMode(true);
    if (hasChildRoute) void router.navigate({ to: '/dashboard/jobs', search: listSearch });
  }

  if (isDedicatedJobFormRoute || isDedicatedTaskRoute || isReportsRoute) {
    return <Outlet />;
  }

  // NOT-297: when a job is open, fall through to the full-page detail even if the
  // Board is the active view — otherwise the board short-circuit would hide the
  // detail. "‹ Jobs" returns here with no child route and the board renders again.
  if (boardMode && !hasChildRoute && !hotspotPostalArea && !isServiceCallsWorkspace) {
    return (
      <JobsWorkspaceShell>
        <JobsBoardView
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onViewChange={(mode) => {
            try {
              localStorage.setItem('jobs-view', mode);
            } catch {
              /* no-op */
            }
            if (mode !== 'board') setBoardMode(false);
          }}
        />
      </JobsWorkspaceShell>
    );
  }

  return (
    <JobsWorkspaceShell>
      <JobsNewButtonSlot
        hidden={isFormMode || hasChildRoute}
        serviceCalls={isServiceCallsWorkspace}
      />
      <EntityIndexLayout
        title={isServiceCallsWorkspace ? 'Service calls' : 'Jobs'}
        storageKey="jobs-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({
            to: isServiceCallsWorkspace ? '/dashboard/jobs/service-calls' : '/dashboard/jobs',
            search: listSearch,
          })
        }
        extraViewOptions={
          isServiceCallsWorkspace
            ? undefined
            : [{ value: 'board', icon: Kanban, title: 'Jobs board view', label: 'Board' }]
        }
        onExtraViewChange={(value) => {
          if (value === 'board') showBoard();
        }}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass={
          isFormMode ? 'w-[95vw] sm:max-w-[1200px]' : 'w-[620px] sm:max-w-[620px]'
        }
        // NOT-273: give the 2-col job detail (NOT-297) full width by hiding the
        // list pane whenever a detail child route is active (a job is selected),
        // not just in form mode. Effectively full-page without a route
        // restructure. The list returns when navigating back to the index.
        hideListInSplit={isFormMode || hasChildRoute}
        // NOT-297: the List (split master-detail) view was removed — jobs show
        // only Table + Board. The job detail is a dedicated full-page view, so in
        // Table mode it renders full-page in the table area (not the side sheet).
        disableSplitView
        fullPageFormInTable
        tableView={() => (
          <JobTableView
            key={[
              routeSearch.q,
              hotspotPostalArea,
              hotspotCountry,
              hotspotRegionCode,
              filters.dateFrom,
              filters.dateTo,
              isServiceCallsRoute,
            ].join(':')}
            externalSearch={routeSearch.q ?? undefined}
            initialServiceCall={filters.serviceCall}
            serviceCallOnly={isServiceCallsRoute}
            hotspotPostalArea={hotspotPostalArea}
            hotspotJobTypeId={hotspotJobTypeId}
            hotspotTechnicianId={hotspotTechnicianId}
            hotspotCountry={hotspotCountry}
            hotspotRegionCode={hotspotRegionCode}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onHotspotPeriodChange={(dateFrom, dateTo) => handleFiltersChange({ dateFrom, dateTo })}
            onOpen={(id) =>
              void router.navigate({
                to: '/dashboard/jobs/$id',
                params: { id },
                search: listSearch,
              })
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden flex flex-col flex-1 min-h-0">
            {hasChildRoute ? (
              <Outlet />
            ) : (
              <JobListPanel
                selectedId={selectedId}
                onSelect={handleSelect}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                serviceCallOnly={isServiceCallsRoute}
                hotspotPostalArea={hotspotPostalArea}
                hotspotJobTypeId={hotspotJobTypeId}
                hotspotTechnicianId={hotspotTechnicianId}
                hotspotCountry={hotspotCountry}
                hotspotRegionCode={hotspotRegionCode}
              />
            )}
          </div>
        }
      />
    </JobsWorkspaceShell>
  );
}

function JobsNewButtonSlot({ hidden, serviceCalls }: { hidden: boolean; serviceCalls: boolean }) {
  const canViewJobs = useHasPermission('jobs:view');
  const canCreateJobs = useHasPermission('jobs:create');
  const canCreateBookings = useHasPermission('bookings:create');
  const newButton = useMemo(() => {
    if (hidden || (serviceCalls ? !canCreateBookings : !canCreateJobs)) return null;
    if (serviceCalls) {
      return (
        <Button size="sm" title="New service call" asChild>
          <Link to="/dashboard/bookings/new" search={{ returnTo: 'service-calls' }}>
            <Plus size={14} />
            New service call
          </Link>
        </Button>
      );
    }
    return (
      <Button size="sm" title="Add job" asChild>
        <Link to="/dashboard/jobs/new" search={{ contactId: undefined }}>
          <Plus size={14} />
          Add job
        </Link>
      </Button>
    );
  }, [canCreateBookings, canCreateJobs, hidden, serviceCalls]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canViewJobs) return null;
    return (
      <Button variant="outline" size="sm" title="Reports" asChild>
        <Link to="/dashboard/jobs/reports">
          <BarChart3 size={14} />
          Reports
        </Link>
      </Button>
    );
  }, [hidden, canViewJobs]);

  useWorkspaceHeader({ newButton, toolbarActions }, [newButton, toolbarActions]);
  return null;
}

interface JobsBoardViewProps {
  filters: JobListFilters;
  onFiltersChange: (patch: Partial<JobListFilters>) => void;
  onViewChange: (mode: ViewMode) => void;
}

function JobsBoardView({ filters, onFiltersChange, onViewChange }: JobsBoardViewProps) {
  const [showClosed, setShowClosed] = useState(false);
  const canCreateJobs = useHasPermission('jobs:create');
  const { data: businessUnits = [] } = useBusinessUnits('active');

  const { data: pipelines = [] } = useJobPipelines();
  const defaultPipelineId = pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? null;
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const activePipelineId = selectedPipelineId ?? defaultPipelineId;

  const toolbarActions = useMemo(
    () => (
      <>
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={filters.q}
            onChange={(event) => onFiltersChange({ q: event.target.value, page: 1 })}
            placeholder="Search jobs..."
            className="h-8 w-48 pl-8 text-sm"
          />
        </div>
        {pipelines.length > 1 && (
          <Select
            value={activePipelineId ?? undefined}
            onValueChange={(v) => setSelectedPipelineId(v)}
          >
            <SelectTrigger className="h-8 w-44 text-sm">
              <GitBranch size={13} className="mr-1.5 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Pipeline" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                  {p.isDefault ? ' (default)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          value={filters.status}
          onValueChange={(value) =>
            onFiltersChange({ status: value as JobListFilters['status'], page: 1 })
          }
        >
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {JOB_STATUS_FILTERS.map((filter) => (
              <SelectItem key={filter.value} value={filter.value}>
                {filter.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showClosed ? 'secondary' : 'ghost'}
          size="icon-sm"
          onClick={() => setShowClosed((v) => !v)}
          title={showClosed ? 'Hide terminal stages' : 'Show terminal stages'}
          aria-label={showClosed ? 'Hide terminal stages' : 'Show terminal stages'}
        >
          {showClosed ? <EyeOff size={14} /> : <Eye size={14} />}
        </Button>
      </>
    ),
    [activePipelineId, filters.q, filters.status, onFiltersChange, pipelines, showClosed],
  );
  const boardViewToggle = useMemo(
    () => (
      <EntityViewToggle
        value="board"
        onChange={(mode) => onViewChange(mode as ViewMode)}
        options={[
          { value: 'table', icon: Table2, title: 'Jobs table view', label: 'Table' },
          { value: 'board', icon: Kanban, title: 'Jobs board view', label: 'Board' },
        ]}
        showLabels
        activeTone="muted"
      />
    ),
    [onViewChange],
  );
  const boardNewButton = useMemo(() => {
    if (!canCreateJobs) return null;
    return (
      <Button size="sm" title="Add job" asChild>
        <Link to="/dashboard/jobs/new" search={{ contactId: undefined }}>
          <Plus size={14} />
          Add job
        </Link>
      </Button>
    );
  }, [canCreateJobs]);

  useWorkspaceHeader(
    {
      toolbarActions,
      viewToggle: boardViewToggle,
      newButton: boardNewButton,
    },
    [boardNewButton, boardViewToggle, toolbarActions],
  );

  return (
    <>
      <Fullbleed />
      <JobsBoardLens
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        q={filters.q}
        status={
          filters.status !== 'all' &&
          filters.status !== 'PAST_DUE' &&
          filters.status !== 'SUSPENDED'
            ? filters.status
            : undefined
        }
        recurringStatus={
          filters.status === 'PAST_DUE' || filters.status === 'SUSPENDED'
            ? filters.status
            : undefined
        }
        businessUnitId={
          businessUnits.length > 0 && filters.businessUnitId !== 'all'
            ? filters.businessUnitId
            : undefined
        }
        pipelineId={activePipelineId}
        showClosed={showClosed}
      />
    </>
  );
}
